import { SLIDE_HEIGHT, SLIDE_WIDTH } from '../schemas/slides.js';
/** Default font size per text role, in canvas pixels — what text falls back to when its runs set
 * no size (decks laid out before TYPE_SCALE, and text boxes added in the editor). */
export const ROLE_FONT_SIZE = {
    title: 76,
    subtitle: 44,
    body: 36,
    caption: 26,
};
/** Line height per text role. Display type is set tight; running text gets room to breathe. The
 * HTML renderer and the PPTX export both read this. */
export const ROLE_LINE_HEIGHT = {
    title: 1.05,
    subtitle: 1.25,
    body: 1.25,
    caption: 1.25,
};
/**
 * The sizes generated slides use, in canvas pixels. Display type does the visible work and body
 * text is the one small size — nothing in between, so hierarchy comes from contrast rather than
 * a ladder of near-equal steps. Titles shrink from these to fit (fitFontSize).
 */
const TYPE_SCALE = {
    /** The opening slide's title. */
    hero: 128,
    /** Section dividers, closing and statement slides, and quotes. */
    display: 104,
    /** Content slide titles. */
    headline: 72,
    body: ROLE_FONT_SIZE.body,
    /** Section numbers and the figures of a stats slide — larger than anything else. */
    numeral: 220,
};
/** Body text size per density: a focused slide says less, so it says it larger. undefined leaves
 * the body role's default. */
const DENSITY_BODY_SIZE = {
    focused: 44,
    balanced: undefined,
    detailed: 30,
};
const MARGIN = 120;
const CONTENT_WIDTH = SLIDE_WIDTH - MARGIN * 2;
/** Running text is kept to about 65 characters a line at body size, however wide the slide. */
const MEASURE = 1180;
const TITLE_Y = 96;
const TITLE_H = 200;
const BODY_Y = 360;
const BODY_H = SLIDE_HEIGHT - BODY_Y - MARGIN;
/** The side of a visual_left / visual_right slide given to the visual, bled to the slide edge. */
const PANEL_W = 840;
/** Where a content slide's style mark sits: in the top margin, above the title band. */
const MARK_Y = TITLE_Y - 20;
/** A short random id for slides and elements — unique within a deck is all that's needed. */
export function newSlideId(prefix = 'el') {
    return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}
/** Splits `**bold**` and `*italic*` markers into runs. Anything unmatched stays literal. */
export function parseInlineMarkdown(text) {
    const runs = [];
    const pattern = /\*\*(.+?)\*\*|\*(.+?)\*/g;
    let last = 0;
    for (const match of text.matchAll(pattern)) {
        const index = match.index ?? 0;
        if (index > last)
            runs.push({ text: text.slice(last, index) });
        if (match[1] !== undefined)
            runs.push({ text: match[1], bold: true });
        else
            runs.push({ text: match[2], italic: true });
        last = index + match[0].length;
    }
    if (last < text.length)
        runs.push({ text: text.slice(last) });
    return runs.length ? runs : [{ text: '' }];
}
/** The design's slide style. Designs saved before styles existed have none: minimal. */
export function designStyle(design) {
    return design.style ?? 'minimal';
}
function textElement(box, role, paragraphs, extra = {}) {
    return {
        id: newSlideId('text'),
        type: 'text',
        role,
        paragraphs,
        verticalAlign: 'top',
        ...box,
        ...extra,
    };
}
function plain(text, run = {}, align) {
    return {
        runs: parseInlineMarkdown(text).map((r) => ({ ...run, ...r })),
        ...(align ? { align } : {}),
    };
}
function bulletParagraphs(bullets, fontSize) {
    return bullets.map((b) => ({
        runs: parseInlineMarkdown(b).map((r) => (fontSize ? { ...r, fontSize } : r)),
        bullet: true,
    }));
}
function recolor(paragraphs, color) {
    return paragraphs.map((p) => ({ ...p, runs: p.runs.map((r) => ({ ...r, color })) }));
}
/** `from` moved `amount` (0-1) of the way towards `to`. */
export function mixColor(from, to, amount) {
    const channel = (hex, i) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
    return `#${[0, 1, 2]
        .map((i) => Math.round(channel(from, i) + (channel(to, i) - channel(from, i)) * amount)
        .toString(16)
        .padStart(2, '0'))
        .join('')}`;
}
/** Whether a color is closer to black than to white, by perceived luminance. */
function isDark(hex) {
    const c = (i) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
    return 0.299 * c(0) + 0.587 * c(1) + 0.114 * c(2) < 128;
}
/** A surface one small step in tone off the slide's ground — how a panel or a divider slide is set
 * apart, instead of a border or a shadow. */
function tintColor(design, amount = 0.05) {
    return mixColor(design.backgroundColor, design.textColor, amount);
}
/** The raised surface of the soft style: near-white on a light ground, a lifted tone on a dark one. */
function cardColor(design) {
    return isDark(design.backgroundColor)
        ? tintColor(design, 0.08)
        : mixColor(design.backgroundColor, '#ffffff', 0.7);
}
function shape(kind, box, fill, stroke = null) {
    return {
        id: newSlideId('shape'),
        type: 'shape',
        shape: kind,
        ...box,
        fill,
        stroke: stroke?.color ?? null,
        strokeWidth: stroke?.width ?? 0,
    };
}
function panel(design, box) {
    return shape('rect', box, tintColor(design));
}
/** Heading fonts are bold and wide: roughly this many em per character, on average. */
const HEADING_CHAR_EM = 0.6;
/** How many lines `text` wraps to at `size` px in a box `width` wide — a word-by-word estimate
 * from an average character width, which is all layout needs to pick a size. */
function estimateLines(text, width, size) {
    const perLine = Math.max(1, Math.floor(width / (size * HEADING_CHAR_EM)));
    let lines = 1;
    let used = 0;
    for (const word of text.split(/\s+/).filter(Boolean)) {
        const need = used === 0 ? word.length : used + 1 + word.length;
        if (need <= perLine)
            used = need;
        else {
            lines += Math.ceil(word.length / perLine);
            used = word.length % perLine || perLine;
        }
    }
    return lines;
}
/** The largest font size, from `max` down to `min` px, at which title text should fit a w x h box. */
export function fitFontSize(text, w, h, max, min = Math.round(max * 0.6)) {
    for (let size = max; size > min; size -= 2) {
        if (estimateLines(text, w, size) * size * ROLE_LINE_HEIGHT.title <= h)
            return size;
    }
    return min;
}
/** How tall display text set at `size` in a box `w` wide is, capped at `maxH`. */
function displayHeight(text, w, size, maxH) {
    return Math.min(maxH, Math.ceil(estimateLines(text, w, size) * size * ROLE_LINE_HEIGHT.title));
}
/** Display text in the heading font, at the largest size up to `max` that fits the box. The
 * editorial style sets display type in regular weight, the others in bold. */
function displayText(ctx, text, box, max, opts = {}) {
    const fontSize = fitFontSize(text, box.w, box.h, max, opts.min);
    const bold = opts.bold ?? (ctx.style === 'editorial' ? false : undefined);
    return textElement(box, 'title', [
        plain(text, {
            fontSize,
            ...(opts.color ? { color: opts.color } : {}),
            ...(bold === false ? { bold: false } : {}),
        }),
    ], { verticalAlign: opts.verticalAlign ?? 'bottom' });
}
/** A content slide's title, bottom-aligned in the same band on every slide so the body below it
 * always starts at BODY_Y. */
function titleBlock(ctx, slide, box = { x: MARGIN, w: CONTENT_WIDTH }) {
    return displayText(ctx, slide.title, { ...box, y: TITLE_Y, h: TITLE_H }, TYPE_SCALE.headline);
}
/** A big number — a section's or a step's — in the heading font. */
function numeral(ctx, n, box, fontSize, color) {
    return textElement(box, 'title', [
        {
            runs: [
                {
                    text: String(n).padStart(2, '0'),
                    fontSize,
                    color,
                    ...(ctx.style === 'editorial' ? { bold: false } : {}),
                },
            ],
        },
    ], { verticalAlign: 'bottom' });
}
/**
 * The style's small recurring mark, centred on `y` and starting at `x` — what ties the content
 * slides together: an accent bar (signal), an accent square on a rule `w` wide (swiss), an accent
 * hairline (editorial) or a pill (soft). Nothing for minimal.
 */
function styleMark(ctx, x, y, w) {
    const { accentColor: accent, primaryColor: primary } = ctx.design;
    switch (ctx.style) {
        case 'signal':
            return [shape('rect', { x, y: y - 5, w: 72, h: 10 }, accent)];
        case 'swiss':
            return [
                shape('rect', { x, y: y - 10, w: 20, h: 20 }, accent),
                shape('rect', { x: x + 36, y: y - 2, w: w - 36, h: 4 }, primary),
            ];
        case 'editorial':
            return [shape('rect', { x, y: y - 1, w: 64, h: 3 }, accent)];
        case 'soft':
            return [shape('roundRect', { x, y: y - 12, w: 96, h: 24 }, accent)];
        default:
            return [];
    }
}
/** The text body of a content slide: the subtitle as a bold lead-in, then the bullets. */
function bodyParagraphs(ctx, slide) {
    const size = ctx.bodySize ? { fontSize: ctx.bodySize } : {};
    const paragraphs = [];
    if (slide.subtitle)
        paragraphs.push(plain(slide.subtitle, { bold: true, ...size }));
    if (slide.bullets?.length)
        paragraphs.push(...bulletParagraphs(slide.bullets, ctx.bodySize));
    return paragraphs.length ? paragraphs : [plain('')];
}
function visualElement(visual, box, fit = 'contain') {
    if (visual.kind === 'diagram' && visual.svg) {
        return {
            id: newSlideId('diagram'),
            type: 'diagram',
            svg: visual.svg,
            description: visual.description,
            ...box,
        };
    }
    return {
        id: newSlideId('image'),
        type: 'image',
        storagePath: null,
        placeholder: `${visual.kind === 'diagram' ? 'Diagram' : visual.kind === 'image' ? 'Image' : 'Illustration'}: ${visual.description}`,
        fit,
        ...box,
    };
}
/** A visual filling `area`, which runs to the slide's edges. A photo bleeds and is cropped to fill;
 * a diagram or illustration sits inset on a tinted panel so none of it is cut off. */
function bledVisual(visual, area, inset, design) {
    if (visual.kind === 'image')
        return [visualElement(visual, area, 'cover')];
    return [panel(design, area), visualElement(visual, inset)];
}
/** The rounded card the soft style sets its ground slides on. */
function softCard(ctx) {
    return shape('roundRect', { x: 80, y: 80, w: SLIDE_WIDTH - 160, h: SLIDE_HEIGHT - 160 }, cardColor(ctx.design));
}
/** The soft style's row of upright pills, short to tall to short, shading from card to accent. */
function softPills(ctx) {
    const heights = [120, 200, 280, 200, 120];
    const strength = [0.35, 0.6, 1, 0.6, 0.35];
    const card = cardColor(ctx.design);
    return heights.map((h, i) => shape('roundRect', { x: 1560 + i * 52, y: SLIDE_HEIGHT / 2 - h / 2, w: 36, h }, mixColor(card, ctx.design.accentColor, strength[i])));
}
/** The opening slide: the title at hero size, in the style's signature composition. */
function openingSlide(ctx, slide) {
    const { design } = ctx;
    const subtitle = (box, extra = {}, color) => slide.subtitle
        ? [textElement(box, 'body', [plain(slide.subtitle, color ? { color } : {})], extra)]
        : [];
    switch (ctx.style) {
        case 'signal': {
            // A solid accent card is the focal point, the title set on it in the ground's color.
            const card = { x: MARGIN, y: MARGIN, w: 1120, h: SLIDE_HEIGHT - MARGIN * 2 };
            const asideX = card.x + card.w + 80;
            return {
                background: design.backgroundColor,
                elements: [
                    shape('rect', card, design.accentColor),
                    displayText(ctx, slide.title, { x: card.x + 80, y: card.y + 80, w: card.w - 160, h: card.h - 160 }, TYPE_SCALE.hero, { color: design.backgroundColor }),
                    ...subtitle({ x: asideX, y: card.y + card.h - 240, w: SLIDE_WIDTH - MARGIN - asideX, h: 240 }, { verticalAlign: 'bottom' }),
                ],
            };
        }
        case 'swiss':
            return {
                background: design.backgroundColor,
                elements: [
                    shape('rect', { x: MARGIN, y: MARGIN, w: CONTENT_WIDTH, h: 12 }, design.primaryColor),
                    shape('rect', { x: SLIDE_WIDTH - MARGIN - 300, y: SLIDE_HEIGHT - MARGIN - 300, w: 300, h: 300 }, design.accentColor),
                    displayText(ctx, slide.title, { x: MARGIN, y: 220, w: 1200, h: 540 }, TYPE_SCALE.hero),
                    ...subtitle({ x: MARGIN, y: 800, w: 1200, h: 160 }),
                ],
            };
        case 'editorial':
            return {
                background: design.backgroundColor,
                elements: [
                    shape('ellipse', { x: 1260, y: 180, w: 520, h: 520 }, null, {
                        color: design.accentColor,
                        width: 3,
                    }),
                    shape('ellipse', { x: 1506, y: 744, w: 28, h: 28 }, design.accentColor),
                    displayText(ctx, slide.title, { x: MARGIN, y: 180, w: 1080, h: 540 }, TYPE_SCALE.hero),
                    shape('rect', { x: MARGIN, y: 772, w: 120, h: 3 }, design.accentColor),
                    ...subtitle({ x: MARGIN, y: 808, w: 1080, h: 152 }),
                ],
            };
        case 'soft':
            return {
                background: design.backgroundColor,
                elements: [
                    softCard(ctx),
                    ...softPills(ctx),
                    displayText(ctx, slide.title, { x: 200, y: 180, w: 1240, h: 540 }, TYPE_SCALE.hero),
                    ...subtitle({ x: 200, y: 760, w: 1240, h: 160 }),
                ],
            };
        default: {
            const onPrimary = design.backgroundColor;
            return {
                background: design.primaryColor,
                elements: [
                    displayText(ctx, slide.title, { x: MARGIN, y: 200, w: CONTENT_WIDTH, h: 560 }, TYPE_SCALE.hero, { color: onPrimary }),
                    ...subtitle({ x: MARGIN, y: 800, w: MEASURE, h: 160 }, {}, onPrimary),
                ],
            };
        }
    }
}
/** A divider between parts of the session: its number set large (when it has one), then the title. */
function sectionSlide(ctx, slide, sectionNumber) {
    const { design } = ctx;
    if (ctx.style === 'minimal') {
        const elements = [
            displayText(ctx, slide.title, { x: MARGIN, y: 240, w: CONTENT_WIDTH, h: 400 }, TYPE_SCALE.display),
        ];
        if (slide.subtitle) {
            elements.push(textElement({ x: MARGIN, y: 680, w: MEASURE, h: 200 }, 'body', [plain(slide.subtitle)]));
        }
        return { background: tintColor(design), elements };
    }
    // swiss sets the whole slide on the accent; the other styles keep a ground of their own.
    const onAccent = ctx.style === 'swiss' ? design.backgroundColor : undefined;
    const background = ctx.style === 'swiss'
        ? design.accentColor
        : ctx.style === 'editorial'
            ? tintColor(design)
            : design.backgroundColor;
    const x = ctx.style === 'soft' ? 200 : MARGIN;
    const w = ctx.style === 'soft' ? 1300 : CONTENT_WIDTH;
    const elements = ctx.style === 'soft' ? [softCard(ctx)] : [];
    if (sectionNumber) {
        elements.push(numeral(ctx, sectionNumber, { x, y: ctx.style === 'soft' ? 140 : MARGIN, w: 800, h: 280 }, TYPE_SCALE.numeral, onAccent ?? design.accentColor));
    }
    if (ctx.style === 'editorial') {
        elements.push(shape('rect', { x, y: 440, w: 120, h: 3 }, design.accentColor));
    }
    elements.push(displayText(ctx, slide.title, { x, y: 460, w, h: 320 }, TYPE_SCALE.display, {
        color: onAccent,
    }));
    if (slide.subtitle) {
        elements.push(textElement({ x, y: 810, w: MEASURE, h: 150 }, 'body', [
            plain(slide.subtitle, onAccent ? { color: onAccent } : {}),
        ]));
    }
    return { background, elements };
}
/** The closing slide: the title, with the recap beside it when there is one. */
function closingSlide(ctx, slide) {
    const { design } = ctx;
    // minimal and swiss close on the primary color and signal on its accent card color, with text
    // in the ground's color; editorial and soft stay on their own ground in their usual colors.
    const ground = ctx.style === 'signal'
        ? design.accentColor
        : ctx.style === 'editorial'
            ? tintColor(design)
            : ctx.style === 'soft'
                ? design.backgroundColor
                : design.primaryColor;
    const onGround = ctx.style === 'editorial' || ctx.style === 'soft' ? undefined : design.backgroundColor;
    const raw = [
        ...(slide.subtitle ? [plain(slide.subtitle)] : []),
        ...bulletParagraphs(slide.bullets ?? []),
    ];
    const lines = onGround ? recolor(raw, onGround) : raw;
    const elements = ctx.style === 'soft' ? [softCard(ctx)] : [];
    if (ctx.style === 'swiss') {
        elements.push(shape('rect', { x: MARGIN, y: MARGIN, w: 64, h: 64 }, design.accentColor));
    }
    if (!lines.length) {
        if (ctx.style === 'editorial') {
            elements.push(shape('ellipse', { x: 1260, y: 280, w: 520, h: 520 }, null, {
                color: design.accentColor,
                width: 3,
            }));
        }
        elements.push(displayText(ctx, slide.title, { x: MARGIN, y: 240, w: ctx.style === 'editorial' ? 1080 : CONTENT_WIDTH, h: 520 }, TYPE_SCALE.display, { color: onGround }));
        return { background: ground, elements };
    }
    // Title on the left, the recap on the right, both centred on the slide's middle.
    const titleW = 760;
    const gap = 120;
    if (ctx.style === 'editorial') {
        elements.push(shape('rect', { x: MARGIN + titleW + gap / 2, y: 240, w: 3, h: SLIDE_HEIGHT - 480 }, design.accentColor));
    }
    elements.push(displayText(ctx, slide.title, { x: MARGIN, y: MARGIN, w: titleW, h: SLIDE_HEIGHT - MARGIN * 2 }, TYPE_SCALE.display, { color: onGround, verticalAlign: 'middle' }), textElement({
        x: MARGIN + titleW + gap,
        y: MARGIN,
        w: CONTENT_WIDTH - titleW - gap,
        h: SLIDE_HEIGHT - MARGIN * 2,
    }, 'body', lines, { verticalAlign: 'middle' }));
    return { background: ground, elements };
}
/** One card of a cards slide: a tinted panel (rounded in soft, with an accent edge in signal) or,
 * in swiss and editorial, just a rule above the text. */
function card(ctx, box, item, large) {
    const { design } = ctx;
    const ruled = ctx.style === 'swiss' || ctx.style === 'editorial';
    const pad = ruled ? 0 : 40;
    const top = ruled ? 36 : 40;
    const elements = [];
    if (ctx.style === 'swiss')
        elements.push(shape('rect', { ...box, h: 4 }, design.primaryColor));
    else if (ctx.style === 'editorial')
        elements.push(shape('rect', { ...box, h: 2 }, design.accentColor));
    else if (ctx.style === 'soft')
        elements.push(shape('roundRect', box, cardColor(design)));
    else
        elements.push(shape('rect', box, tintColor(design, ctx.style === 'signal' ? 0.08 : 0.05)));
    if (ctx.style === 'signal')
        elements.push(shape('rect', { ...box, h: 8 }, design.accentColor));
    elements.push(textElement({ x: box.x + pad, y: box.y + top, w: box.w - pad * 2, h: box.h - top - 32 }, 'body', [
        plain(item.heading, { bold: true, color: design.primaryColor, fontSize: large ? 40 : 34 }),
        ...(item.text ? [plain(item.text, { fontSize: large ? 30 : 26 })] : []),
    ]));
    return elements;
}
/** 2-6 cards: one row of up to three, two rows of two for four, two rows of three for more. */
function cardGrid(ctx, items) {
    const n = items.length;
    const cols = n <= 3 ? n : n === 4 ? 2 : 3;
    const rows = Math.ceil(n / cols);
    const gap = 40;
    const w = (CONTENT_WIDTH - gap * (cols - 1)) / cols;
    // A single row stays short of the full body height, so its cards aren't mostly empty.
    const h = rows === 1 ? 420 : (BODY_H - gap) / 2;
    return items.flatMap((item, i) => card(ctx, { x: MARGIN + (i % cols) * (w + gap), y: BODY_Y + Math.floor(i / cols) * (h + gap), w, h }, item, rows === 1));
}
/** 2-5 steps left to right: a large number, a rule, then the step's heading and text. */
function stepRow(ctx, items) {
    const { design } = ctx;
    const gap = 48;
    const w = (CONTENT_WIDTH - gap * (items.length - 1)) / items.length;
    const numberColor = ctx.style === 'minimal' ? design.primaryColor : design.accentColor;
    return items.flatMap((item, i) => {
        const x = MARGIN + i * (w + gap);
        return [
            numeral(ctx, i + 1, { x, y: BODY_Y - 20, w, h: 130 }, 96, numberColor),
            ctx.style === 'soft'
                ? shape('roundRect', { x, y: BODY_Y + 132, w, h: 12 }, cardColor(design))
                : shape('rect', { x, y: BODY_Y + 136, w, h: 4 }, tintColor(design, 0.18)),
            textElement({ x, y: BODY_Y + 176, w, h: BODY_H - 176 }, 'body', [
                plain(item.heading, { bold: true, color: design.primaryColor, fontSize: 34 }),
                ...(item.text ? [plain(item.text, { fontSize: 28 })] : []),
            ]),
        ];
    });
}
/** 1-3 figures side by side at display size, each above its label. */
function statRow(ctx, stats) {
    const { design } = ctx;
    const gap = 80;
    const w = (CONTENT_WIDTH - gap * (stats.length - 1)) / stats.length;
    const max = stats.length === 1 ? 260 : TYPE_SCALE.numeral;
    const color = ctx.style === 'minimal' ? design.primaryColor : design.accentColor;
    return stats.flatMap((stat, i) => {
        const x = MARGIN + i * (w + gap);
        return [
            displayText(ctx, stat.value, { x, y: BODY_Y, w, h: 300 }, max, { color }),
            textElement({ x, y: BODY_Y + 330, w: Math.min(w, MEASURE), h: BODY_H - 330 }, 'body', [
                plain(stat.label, { fontSize: 32 }),
            ]),
        ];
    });
}
/** A slide's items or stats written out as bullets, for a layout that can't show them as such. */
function asBullets(slide) {
    if (slide.items?.length)
        return slide.items.map((it) => (it.text ? `**${it.heading}** — ${it.text}` : it.heading));
    if (slide.stats?.length)
        return slide.stats.map((s) => `**${s.value}** ${s.label}`);
    return slide.bullets ?? [];
}
/** The layout a slide actually gets: the one it asked for, unless its content doesn't fit it. */
function resolveLayout(slide) {
    let layout = slide.layout;
    const items = slide.items?.length ?? 0;
    if (layout === 'steps' && (items < 2 || items > 5))
        layout = 'cards';
    if (layout === 'cards' && items < 2)
        layout = 'bullets';
    if (layout === 'stats' && !slide.stats?.length)
        layout = 'bullets';
    const needsVisual = layout === 'visual_left' || layout === 'visual_right' || layout === 'visual_full';
    if (needsVisual && !slide.visual)
        layout = 'bullets';
    if (layout === 'statement' && slide.bullets?.length)
        layout = 'bullets';
    const textOnly = ['bullets', 'two_columns', 'statement', 'cards', 'steps', 'stats'];
    if (textOnly.includes(layout) && slide.visual)
        layout = 'visual_right';
    return layout;
}
/**
 * Turns one generated slide into positioned elements on the SLIDE_WIDTH x SLIDE_HEIGHT canvas.
 * Deterministic apart from ids, so the same generation always lays out the same way. A layout
 * whose content is missing falls back to bullets (and steps with too many steps to cards); a
 * text-only layout that comes with a visual is upgraded to visual_right rather than dropping it,
 * its cards, steps or stats written out as bullets; a statement with bullets becomes a bullets
 * slide.
 *
 * Three grounds give a deck its rhythm — the opening and closing slides, section dividers and
 * quotes, and the plain background for content — and the design's style decides how each is
 * dressed (see SLIDE_STYLES). minimal adds no decoration at all. Content slides show their
 * number, except where a visual covers its corner; the opening, section and closing slides don't.
 */
export function layoutSlide(slide, design, opts = {}) {
    const ctx = {
        design,
        style: designStyle(design),
        bodySize: DENSITY_BODY_SIZE[opts.density ?? 'balanced'],
    };
    const layout = resolveLayout(slide);
    let background = design.backgroundColor;
    let elements;
    let showNumber = true;
    switch (layout) {
        case 'title':
            ({ background, elements } = openingSlide(ctx, slide));
            showNumber = false;
            break;
        case 'section':
            ({ background, elements } = sectionSlide(ctx, slide, opts.sectionNumber));
            showNumber = false;
            break;
        case 'closing':
            ({ background, elements } = closingSlide(ctx, slide));
            showNumber = false;
            break;
        case 'statement': {
            // The sentence and its support, centred as one group on the slide's middle.
            const w = 1500;
            const size = fitFontSize(slide.title, w, 640, TYPE_SCALE.display);
            // At most the 640 it was fitted to — an overlong sentence runs on past its box, visibly.
            const textH = displayHeight(slide.title, w, size, 640);
            const subtitle = slide.subtitle ? { gap: 60, h: 140 } : { gap: 0, h: 0 };
            const top = Math.max(MARGIN, Math.round((SLIDE_HEIGHT - textH - subtitle.gap - subtitle.h) / 2));
            elements = [
                ...styleMark(ctx, MARGIN, top - 56, w),
                displayText(ctx, slide.title, { x: MARGIN, y: top, w, h: textH }, TYPE_SCALE.display),
            ];
            if (slide.subtitle) {
                elements.push(textElement({ x: MARGIN, y: top + textH + subtitle.gap, w: MEASURE, h: subtitle.h }, 'body', [plain(slide.subtitle)]));
            }
            break;
        }
        case 'two_columns': {
            const columns = slide.columns?.length
                ? slide.columns
                : [{ heading: slide.subtitle ?? '', bullets: slide.bullets ?? [] }];
            const gap = 120;
            const colW = (CONTENT_WIDTH - gap) / 2;
            const size = ctx.bodySize ? { fontSize: ctx.bodySize } : {};
            elements = [
                ...styleMark(ctx, MARGIN, MARK_Y, CONTENT_WIDTH),
                titleBlock(ctx, slide),
                ...columns.map((col, i) => textElement({ x: MARGIN + i * (colW + gap), y: BODY_Y, w: colW, h: BODY_H }, 'body', [
                    plain(col.heading, { bold: true, color: design.primaryColor, ...size }),
                    ...bulletParagraphs(col.bullets, ctx.bodySize),
                ])),
            ];
            break;
        }
        case 'cards':
            elements = [
                ...styleMark(ctx, MARGIN, MARK_Y, CONTENT_WIDTH),
                titleBlock(ctx, slide),
                ...cardGrid(ctx, slide.items),
            ];
            break;
        case 'steps':
            elements = [
                ...styleMark(ctx, MARGIN, MARK_Y, CONTENT_WIDTH),
                titleBlock(ctx, slide),
                ...stepRow(ctx, slide.items),
            ];
            break;
        case 'stats':
            elements = [
                ...styleMark(ctx, MARGIN, MARK_Y, CONTENT_WIDTH),
                titleBlock(ctx, slide),
                ...statRow(ctx, slide.stats),
            ];
            break;
        case 'visual_right':
        case 'visual_left': {
            // The visual takes one side of the slide, edge to edge; the text column takes the other.
            const gap = 100;
            const textW = SLIDE_WIDTH - PANEL_W - MARGIN - gap;
            const panelX = layout === 'visual_right' ? SLIDE_WIDTH - PANEL_W : 0;
            const textX = layout === 'visual_right' ? MARGIN : PANEL_W + gap;
            const inset = 80;
            // A visual on the right covers the corner the slide number goes in.
            showNumber = layout === 'visual_left';
            elements = [
                ...bledVisual(slide.visual, { x: panelX, y: 0, w: PANEL_W, h: SLIDE_HEIGHT }, { x: panelX + inset, y: MARGIN, w: PANEL_W - inset * 2, h: SLIDE_HEIGHT - MARGIN * 2 }, design),
                ...styleMark(ctx, textX, MARK_Y, textW),
                titleBlock(ctx, slide, { x: textX, w: textW }),
                textElement({ x: textX, y: BODY_Y, w: textW, h: BODY_H }, 'body', bodyParagraphs(ctx, { ...slide, bullets: asBullets(slide) })),
            ];
            break;
        }
        case 'visual_full': {
            // The visual runs from below the title to the bottom and side edges of the slide.
            const top = BODY_Y - 40;
            showNumber = false;
            elements = [
                ...styleMark(ctx, MARGIN, MARK_Y, CONTENT_WIDTH),
                titleBlock(ctx, slide),
                ...bledVisual(slide.visual, { x: 0, y: top, w: SLIDE_WIDTH, h: SLIDE_HEIGHT - top }, { x: MARGIN, y: top + 60, w: CONTENT_WIDTH, h: SLIDE_HEIGHT - top - 120 }, design),
            ];
            break;
        }
        case 'quote': {
            // The quote alone is the slide, set as display type; the slide's title stays in the outline.
            background = tintColor(design);
            const quote = slide.quote ?? { text: slide.subtitle ?? slide.title };
            const text = `“${quote.text}”`;
            const box = { x: MARGIN, y: MARGIN, w: 1500, h: 640 };
            // The quote sits at the bottom of its box; the mark goes just above where it starts.
            const size = fitFontSize(text, box.w, box.h, TYPE_SCALE.display, 44);
            const top = box.y + box.h - displayHeight(text, box.w, size, box.h);
            elements = [
                ...styleMark(ctx, MARGIN, Math.max(MARK_Y, top - 56), box.w),
                displayText(ctx, text, box, TYPE_SCALE.display, { bold: false, min: 44 }),
            ];
            if (quote.attribution) {
                elements.push(textElement({ x: MARGIN, y: 820, w: MEASURE, h: 140 }, 'body', [
                    plain(`— ${quote.attribution}`),
                ]));
            }
            break;
        }
        default: {
            elements = [
                ...styleMark(ctx, MARGIN, MARK_Y, CONTENT_WIDTH),
                titleBlock(ctx, slide),
                textElement({ x: MARGIN, y: BODY_Y, w: MEASURE, h: BODY_H }, 'body', bodyParagraphs(ctx, slide)),
            ];
        }
    }
    return {
        id: newSlideId('slide'),
        background,
        elements,
        speakerNotes: slide.speakerNotes,
        ...(showNumber ? { showNumber } : {}),
    };
}
/** A generated deck laid out slide by slide, its section slides numbered in order. */
export function layoutDeck(slides, design, opts = {}) {
    let sections = 0;
    return slides.map((slide) => layoutSlide(slide, design, {
        ...opts,
        ...(resolveLayout(slide) === 'section' ? { sectionNumber: ++sections } : {}),
    }));
}
//# sourceMappingURL=layout.js.map